import LoadingButton from '@components/Button/LoadingButton';
import { Tooltip } from '@eye-able/ui';
import { IconRadar } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import type { PolicyResponse } from '@http/queries/useFetchPolicies';
import {
  getPolicyRescanDisabledTooltipKey,
  isPolicyRescanDisabled,
  usePolicyRescan,
} from './usePolicyRescan';

/** Schedules an immediate rescan for every domain in a policy's scope. */
export function PolicyRescanControl({ policy }: { policy: PolicyResponse }) {
  const { t } = useInlineTranslation();
  const {
    hasUnlicensedDomains,
    isLicenseUnavailable,
    getApplicableAuditEntryIds,
    getAuditEntryIds,
    areAllApplicableAuditEntriesScheduled,
    isPrivilegedUser,
    isRescanning,
    rescanPolicy,
  } = usePolicyRescan(policy.auditEntryIds);
  const applicableAuditEntryIds = useMemo(
    () => getApplicableAuditEntryIds(policy),
    [getApplicableAuditEntryIds, policy],
  );
  const auditEntryIds = useMemo(
    () => getAuditEntryIds(policy),
    [getAuditEntryIds, policy],
  );
  const areAllApplicableEntriesScheduled = useMemo(
    () => areAllApplicableAuditEntriesScheduled(policy),
    [areAllApplicableAuditEntriesScheduled, policy],
  );
  const disabledTooltipKey = getPolicyRescanDisabledTooltipKey(
    policy,
    applicableAuditEntryIds,
    auditEntryIds,
    isPrivilegedUser,
    areAllApplicableEntriesScheduled,
    hasUnlicensedDomains(policy),
  );
  const isDisabled = isPolicyRescanDisabled(
    policy,
    auditEntryIds,
    isPrivilegedUser,
    isLicenseUnavailable || hasUnlicensedDomains(policy),
  );

  return (
    <Tooltip content={disabledTooltipKey ? t(disabledTooltipKey) : undefined}>
      <span className='eye:inline-flex' tabIndex={isDisabled ? 0 : undefined}>
        <LoadingButton
          type='button'
          variant='yellow'
          size='sm'
          loading={isRescanning}
          disabled={isDisabled}
          onClick={() => void rescanPolicy(policy)}
        >
          <IconRadar aria-hidden />
          {t('checkAgainButton')}
        </LoadingButton>
      </span>
    </Tooltip>
  );
}
